import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface MaterielElectriqueDetailProps {
  t: any;
  isRTL: boolean;
}

interface MaterielElectriqueProduct {
  id: string;
  name: string;
  mainImage: string;
  additionalImages?: string[];
  provider?: string;
  description?: string | string[];
  technicalSpecs?: { [key: string]: string };
  characteristics?: { 
    [key: string]: {
      type: string;
      data: { [key: string]: string }[];
    } | string;
  };
  category: string;
  subcategory?: string;
}

const ProductDetailContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: #ffffff;
  min-height: 100vh;
  padding-top: 140px;
  
  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 100px;
  }
`;

const ProductHeader = styled.div`
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ImagesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const MainImageContainer = styled.div`
  width: 100%;
  max-width: 700px;
  height: 450px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    height: 300px;
    padding: 1rem;
    border-radius: 12px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ThumbnailImage = styled.div<{ $isSelected: boolean }>`
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid ${props => props.$isSelected ? '#dc2626' : '#e2e8f0'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &:hover {
    border-color: #dc2626;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

const ProviderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 2rem;
  }
`;

const ProviderLogo = styled.img`
  max-width: 150px;
    height: auto;
    object-fit: contain;
  
  @media (max-width: 768px) {
      max-width: 100px;
  }
`;

const SpecificationsSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
    overflow-x: auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
  border-bottom: 3px solid #dc2626;
  padding-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const SpecsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  thead tr {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    min-width: 600px;
  }
`;

const TableHeader = styled.th`
  color: white;
  padding: 1rem 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8rem;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
  background: #f8fafc;
  }
  
  &:hover {
    background: #f1f5f9;
  }
`;

const TableCell = styled.td`
  padding: 1rem 0.75rem;
  text-align: center;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.95rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: white;
  border-radius: 16px;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const LoadingText = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const MaterielElectriqueDetail: React.FC<MaterielElectriqueDetailProps> = ({ t, isRTL }) => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<MaterielElectriqueProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setSelectedImageIndex(0); // Reset to first image when loading new product
        const productData = await FirebaseService.getProduct(productId);
        if (productData) {
          setProduct(productData as MaterielElectriqueProduct);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <ProductDetailContainer>
          <LoadingContainer>
          <LoadingText>Chargement des détails du produit...</LoadingText>
          </LoadingContainer>
      </ProductDetailContainer>
    );
  }

  if (error || !product) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorText>{error || 'Produit non trouvé'}</ErrorText>
        </ErrorContainer>
      </ProductDetailContainer>
    );
  }

  const allImages = [product.mainImage, ...(product.additionalImages || [])].filter(Boolean);

  // Extract tables from characteristics
  const tables: Array<{ name: string; data: { [key: string]: string }[] }> = [];
  if (product.characteristics) {
    Object.entries(product.characteristics).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'table') {
        tables.push({
          name: key,
          data: value.data || []
        });
      }
    });
  }

  return (
    <ProductDetailContainer>
      <ProductHeader>
        <ProductTitle>
          {product.name || 'Matériel Électrique'}
        </ProductTitle>
        <ProductDescription>
          {product.description 
            ? (Array.isArray(product.description) ? product.description.join(' ') : product.description)
            : 'Description détaillée du produit'
          }
        </ProductDescription>
      </ProductHeader>

      {allImages.length > 0 && (
        <ImagesSection>
          <MainImageContainer>
            <MainImage
              src={allImages[selectedImageIndex]}
              alt={`${product.name} - Image ${selectedImageIndex + 1}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/images/logo.png';
              }}
            />
          </MainImageContainer>
          
          {allImages.length > 1 && (
            <ThumbnailsContainer>
              {allImages.map((image, index) => (
                <ThumbnailImage
                  key={index}
                  $isSelected={selectedImageIndex === index}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={image}
                    alt={`${product.name} - Thumbnail ${index + 1}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                      target.src = '/images/logo.png';
                    }}
                  />
                </ThumbnailImage>
              ))}
            </ThumbnailsContainer>
          )}
        </ImagesSection>
      )}

      {product.provider && (
        <ProviderSection>
          <ProviderLogo
            src={`/images/partners/${product.provider.toLowerCase()}.png`}
            alt={product.provider}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
              target.src = '/images/logo.png';
            }}
          />
        </ProviderSection>
      )}

      {tables.map((table, tableIndex) => {
        if (table.data.length === 0) return null;
        
        // Get column keys and sort them numerically (1-8)
        let columnKeys = Object.keys(table.data[0]);
        columnKeys = columnKeys.sort((a, b) => {
          const numA = parseInt(a);
          const numB = parseInt(b);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return a.localeCompare(b);
        });
        
        // First row contains the header values
        const firstRow = table.data[0];
        const dataRows = table.data.slice(1); // All rows except the first one
        
        return (
          <SpecificationsSection key={tableIndex}>
            <SectionTitle>{table.name}</SectionTitle>
            <SpecsTable>
              <thead>
                <tr>
                  {columnKeys.map((key, index) => (
                    <TableHeader key={index}>{firstRow[key] || '—'}</TableHeader>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columnKeys.map((key, cellIndex) => (
                      <TableCell key={cellIndex}>{row[key] || '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </tbody>
            </SpecsTable>
          </SpecificationsSection>
        );
      })}
    </ProductDetailContainer>
  );
};

export default MaterielElectriqueDetail;
